/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { useContext } from 'react';
import { getCurrentUser, isStaff } from '../../selectors/user';
import { getQuestionProgress } from '../../selectors/survey';
import { loadSurvey, unloadSurveyListSummary } from '../../actions/survey';
import { reloadSurveyList } from '../../slice/initiativeSurveyListSlice';
import { reloadInitiative } from '../../actions/initiative';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { SurveyContext, SurveyContextLoadedProps } from './SurveyContainer';
import { CompleteButton } from '@features/survey/button/CompleteButton';
import './style.scss';
import SurveyProgress, { AnimationVariant } from '@features/survey-progress';
import { AIAutoAnswerButtons } from '@features/ai-auto-answer';
import { InitiativePermissions } from '@services/permissions/InitiativePermissions';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { getRootConfig } from '@selectors/globalData';
import { SurveyPermissions } from '@services/permissions/SurveyPermissions';
import { AIAutoAnswerStreaming } from '../ai-auto-answer/AIAutoAnswerStreaming';
import { useToggle } from '../../hooks/useToggle';

export default function SurveyOverviewHeader({ handleNavigate }: { handleNavigate: (page: string, extras: { [key: string]: string }) => void }) {
  const { surveyData } = useContext(SurveyContext) as SurveyContextLoadedProps;
  const dispatch = useAppDispatch();
  const [isOpenStreaming, toggleStreaming ] = useToggle(false);

  const isUserStaff = useAppSelector(isStaff);
  const currentUser = useAppSelector(getCurrentUser);
  const isManager = currentUser && InitiativePermissions.canManageInitiative(currentUser, surveyData.initiativeId);
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessAIAutoAnswer = FeaturePermissions.canAccessAIAutoAnswer(rootConfig);
  const canAccessAIDocumentLibrary = FeaturePermissions.canAccessAIDocumentLibrary(rootConfig);

  const progressStats = useAppSelector(getQuestionProgress);
  const isUserAdmin = useAppSelector((state) => {
    if (!state.survey.loaded) {
      return false;
    }
    const currentUser = getCurrentUser(state);

    return Boolean(currentUser && SurveyPermissions.canManage(state.survey.data, currentUser));
  });

  const handleReload = async (): Promise<void> => {
    if (surveyData._id) {
      await Promise.all([
        dispatch(loadSurvey(surveyData._id, true, true)),
        dispatch(unloadSurveyListSummary()),
        dispatch(reloadSurveyList()),
        dispatch(reloadInitiative()),
      ]);
    }
  };

  return (
    <>
      <div className='w-100 d-flex' data-testid='survey-overview-header'>
        <div className='flex-grow-1 pr-3'>
          {isUserStaff && isManager && canAccessAIAutoAnswer && surveyData.type === SurveyType.Default ? (
            <AIAutoAnswerButtons
              survey={surveyData}
              initiativeId={surveyData.initiativeId}
              progressStats={progressStats}
              canAccessAIDocumentLibrary={canAccessAIDocumentLibrary}
              toggleStreaming={toggleStreaming}
              handleViewProgress={(batchId?: string) => handleNavigate('auto-answer', batchId ? { batchId } : {})}
            />
          ) : null}

          <SurveyProgress {...progressStats} animationVariant={AnimationVariant.Animated} />
          <div className='text-sm text-right text-ThemeTextMedium mt-1'>
            <span>
              answered{' '}
              <span className='dont_translate'>
                {progressStats.completed}/{progressStats.count}
              </span>
            </span>
            <span className='px-3'>|</span>
            <span>
              verified{' '}
              <span className='dont_translate'>
                {progressStats.verified}/{progressStats.count}
              </span>
            </span>
          </div>
        </div>
        <div className='flex-grow-0'>
          <CompleteButton
            survey={surveyData}
            isUserAdmin={isUserAdmin}
            handleReload={handleReload}
            classes={{ icon: 'fs-3 ' }}
          />
        </div>
      </div>
      {isOpenStreaming ? (
        <AIAutoAnswerStreaming
        initiativeId={surveyData.initiativeId} surveyId={surveyData._id} toggleStreaming={toggleStreaming} />
      ) : null}
    </>
  );
}
